import { X as CloseIcon, Menu02, Sliders01 } from '#app/components/icons/solid'
import {
	Button as AriaButton,
	Dialog,
	DialogTrigger,
	Link,
	Modal,
	ModalOverlay,
	type Color,
	type ColorSpace,
} from 'react-aria-components'
import { unstable_useRoute, useLocation } from 'react-router'
import { CustomizePanel } from '#app/components/customize-panel.tsx'
import Logo from '#app/components/logo.tsx'
import { Button } from '#app/components/shared/buttons/button.tsx'
import { DOMAIN_CONFIGS } from '#app/config/domains.ts'
import { cx } from '#app/utils/cx'
import { usePreserveNuqsParams } from '#app/utils/navigation.ts'

export const MobileNavigationHeader = ({
	children,
	strokeWidth,
	setStrokeWidth,
	color,
	setColor,
	colorSpace,
	setColorSpace,
}: {
	children: React.ReactNode
	strokeWidth?: string
	setStrokeWidth?: (strokeWidth: string) => void
	color?: Color
	setColor?: (color: Color) => void
	colorSpace?: ColorSpace | 'hex'
	setColorSpace?: (colorSpace: ColorSpace | 'hex') => void
}) => {
	const buildUrl = usePreserveNuqsParams(['redirectTo'])
	const { pathname } = useLocation()
	const rootData = unstable_useRoute('root')?.loaderData
	const domainConfig = rootData?.domainConfig || DOMAIN_CONFIGS.localhost

	return (
		<>
			<header className="border-secondary bg-primary flex h-16 items-center justify-between border-b py-3 pr-2 pl-4 lg:hidden">
				<Link
					href={buildUrl('/')}
					className="group bg-primary outline-focus-ring hover:bg-primary_hover relative flex cursor-pointer items-center rounded-md px-3 py-2 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2"
				>
					<Logo
						variant={domainConfig.logoType}
						className="text-fg-quaternary transition-inherit-all mr-3 size-6 shrink-0"
					/>
					<span className="text-md text-secondary transition-inherit-all group-hover:text-secondary_hover flex-1 font-semibold">
						{domainConfig.name}
					</span>
				</Link>

				<div className="flex items-center gap-2">
					{strokeWidth &&
						setStrokeWidth &&
						color &&
						setColor &&
						colorSpace &&
						setColorSpace && (
							<DialogTrigger>
								<Button
									color="secondary"
									size="md"
									iconLeading={<Sliders01 data-icon />}
								>
									Customize
								</Button>

								<ModalOverlay
									isDismissable
									className={({ isEntering, isExiting }) =>
										cx(
											'bg-overlay/70 fixed inset-0 z-50 pr-16 backdrop-blur-md lg:hidden',
											isEntering &&
												'animate-in fade-in duration-300 ease-in-out',
											isExiting &&
												'animate-out fade-out duration-200 ease-in-out',
										)
									}
								>
									{({ state }) => (
										<>
											<AriaButton
												aria-label="Close navigation menu"
												onPress={() => state.close()}
												className="text-fg-white/70 outline-focus-ring hover:text-fg-white fixed top-3 right-2 flex items-center justify-center rounded-lg p-2.5 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2"
											>
												<CloseIcon className="size-5" />
											</AriaButton>

											<Modal className="w-full cursor-auto will-change-transform">
												<Dialog className="h-dvh outline-hidden focus:outline-hidden">
													<div className="border-secondary bg-primary flex h-full w-full overflow-auto pt-3 shadow-xs md:border-r">
														<CustomizePanel
															strokeWidth={strokeWidth}
															setStrokeWidth={setStrokeWidth}
															color={color}
															setColor={setColor}
															colorSpace={colorSpace}
															setColorSpace={setColorSpace}
														/>
													</div>
												</Dialog>
											</Modal>
										</>
									)}
								</ModalOverlay>
							</DialogTrigger>
						)}
					<DialogTrigger key={pathname}>
						<AriaButton
							aria-label="Expand navigation menu"
							className="group bg-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover flex items-center justify-center rounded-lg p-2.5 focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							<Menu02 className="size-5 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
							<CloseIcon className="absolute size-5 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
						</AriaButton>
						<ModalOverlay
							isDismissable
							className={({ isEntering, isExiting }) =>
								cx(
									'bg-overlay/70 fixed inset-0 z-50 pr-16 backdrop-blur-md lg:hidden',
									isEntering && 'animate-in fade-in duration-300 ease-in-out',
									isExiting && 'animate-out fade-out duration-200 ease-in-out',
								)
							}
						>
							{({ state }) => (
								<>
									<AriaButton
										aria-label="Close navigation menu"
										onPress={() => state.close()}
										className="text-fg-white/70 outline-focus-ring hover:text-fg-white fixed top-3 right-2 flex items-center justify-center rounded-lg p-2.5 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2"
									>
										<CloseIcon className="size-5" />
									</AriaButton>

									<Modal className="w-full cursor-auto will-change-transform">
										<Dialog className="h-dvh outline-hidden focus:outline-hidden">
											{children}
										</Dialog>
									</Modal>
								</>
							)}
						</ModalOverlay>
					</DialogTrigger>
				</div>
			</header>
		</>
	)
}
